"use client";

import type { ChatPlayerAutoPlaybackFrame } from "./chat-player-clock";
import type { ChatPlayerCompiledChat } from "./chat-player-types";

import { useEffect } from "react";

import {
  createAutoPlaybackFrame,
  getInitialAutoPlaybackTimeMs,
  getInitialMediaPlaybackTimeMs,
  getMediaPlaybackBoundaryDelayMs,
  getNextAutoPlaybackDelayMs,
  getNextAutoPlaybackFrame,
  shouldSyncMediaPlaybackTime,
} from "./chat-player-clock";
import { useChatPlayerPlaybackActions, useChatPlayerPlaybackStore } from "./chat-player-playback-store";

export type ChatPlayerPlayback =
  | {
      type: "auto";
      active?: boolean;
      initialTimeMs?: number;
      loop?: boolean;
      loopResetMs?: number;
    }
  | {
      type: "media";
      mediaElement: HTMLMediaElement | null;
      fallbackTimeMs?: number;
    };

const MEDIA_BOUNDARY_SYNC_EVENTS = ["timeupdate", "ratechange"] as const;
const MEDIA_FORCE_SYNC_EVENTS = ["seeked", "loadedmetadata"] as const;
const MEDIA_START_EVENTS = ["play", "playing"] as const;
const MEDIA_STOP_EVENTS = ["pause", "ended"] as const;

export function getInitialChatPlayerPlaybackTimeMs(playback: ChatPlayerPlayback, durationMs: number) {
  switch (playback.type) {
    case "auto":
      return getInitialAutoPlaybackTimeMs({
        durationMs,
        initialTimeMs: playback.initialTimeMs ?? 0,
      });
    case "media":
      return getInitialMediaPlaybackTimeMs({
        currentTimeMs: playback.mediaElement ? playback.mediaElement.currentTime * 1000 : undefined,
        durationMs,
        fallbackTimeMs: playback.fallbackTimeMs ?? 0,
      });
  }
}

export function ChatPlayerPlaybackDriver({
  chat,
  playback,
}: {
  chat: ChatPlayerCompiledChat;
  playback: ChatPlayerPlayback;
}) {
  switch (playback.type) {
    case "auto":
      return <AutoPlaybackDriver chat={chat} playback={playback} />;
    case "media":
      return <MediaPlaybackDriver chat={chat} playback={playback} />;
  }
}

function AutoPlaybackDriver({
  chat,
  playback,
}: {
  chat: ChatPlayerCompiledChat;
  playback: Extract<ChatPlayerPlayback, { type: "auto" }>;
}) {
  const store = useChatPlayerPlaybackStore();
  const actions = useChatPlayerPlaybackActions();
  const active = playback.active ?? true;
  const initialTimeMs = playback.initialTimeMs ?? 0;
  const loop = Boolean(playback.loop);
  const loopResetMs = playback.loopResetMs ?? 0;

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let currentFrame = createAutoPlaybackFrame(
      getInitialAutoPlaybackTimeMs({ durationMs: chat.durationMs, initialTimeMs }),
      store.getState().playbackKey,
    );
    let previousFrameMs = performance.now();

    const clearTimer = () => {
      if (timerId === null) {
        return;
      }

      clearTimeout(timerId);
      timerId = null;
    };
    const syncFrame = (frame: ChatPlayerAutoPlaybackFrame) => {
      currentFrame = frame;
      actions.syncFrame({ timeMs: frame.timeMs, playbackKey: frame.playbackKey });
    };
    const scheduleNextFrame = () => {
      const delayMs = getNextAutoPlaybackDelayMs(chat, currentFrame);

      if (delayMs === null) {
        return;
      }

      timerId = setTimeout(tick, delayMs);
    };
    const tick = () => {
      const frameMs = performance.now();
      const nextFrame = getNextAutoPlaybackFrame({
        currentFrame,
        deltaMs: frameMs - previousFrameMs,
        durationMs: chat.durationMs,
        loop,
        loopResetMs,
      });

      previousFrameMs = frameMs;
      syncFrame(nextFrame);

      if (nextFrame.shouldContinue) {
        scheduleNextFrame();
      }
    };

    syncFrame(currentFrame);

    if (!active || (!loop && currentFrame.timeMs >= chat.durationMs)) {
      return clearTimer;
    }

    scheduleNextFrame();

    return clearTimer;
  }, [actions, active, chat, initialTimeMs, loop, loopResetMs, store]);

  return null;
}

function MediaPlaybackDriver({
  chat,
  playback,
}: {
  chat: ChatPlayerCompiledChat;
  playback: Extract<ChatPlayerPlayback, { type: "media" }>;
}) {
  const store = useChatPlayerPlaybackStore();
  const actions = useChatPlayerPlaybackActions();
  const mediaElement = playback.mediaElement;
  const fallbackTimeMs = playback.fallbackTimeMs ?? 0;

  useEffect(() => {
    if (!mediaElement) {
      actions.syncToTime(fallbackTimeMs);
      return;
    }

    let timerId: ReturnType<typeof setTimeout> | null = null;
    const clearTimer = () => {
      if (timerId === null) {
        return;
      }

      clearTimeout(timerId);
      timerId = null;
    };
    const syncToMediaTime = () => {
      const timeMs = mediaElement.currentTime * 1000;

      if (shouldSyncMediaPlaybackTime(chat, store.getState().frame.timeMs, timeMs)) {
        actions.syncToTime(timeMs);
      }
    };
    const forceSyncToMediaTime = () => {
      actions.syncToTime(mediaElement.currentTime * 1000);
    };
    const scheduleNextFrame = () => {
      clearTimer();

      if (mediaElement.paused || mediaElement.ended) {
        return;
      }

      const delayMs = getMediaPlaybackBoundaryDelayMs(chat, mediaElement.currentTime * 1000, mediaElement.playbackRate);

      if (delayMs !== null) {
        timerId = setTimeout(syncAndSchedule, delayMs);
      }
    };
    const syncAndSchedule = () => {
      syncToMediaTime();
      scheduleNextFrame();
    };
    const forceSyncAndSchedule = () => {
      forceSyncToMediaTime();
      scheduleNextFrame();
    };
    const stop = () => {
      clearTimer();
      forceSyncToMediaTime();
    };
    const subscriptions = [
      ...MEDIA_BOUNDARY_SYNC_EVENTS.map(eventName => [eventName, syncAndSchedule] as const),
      ...MEDIA_FORCE_SYNC_EVENTS.map(eventName => [eventName, forceSyncAndSchedule] as const),
      ...MEDIA_START_EVENTS.map(eventName => [eventName, forceSyncAndSchedule] as const),
      ...MEDIA_STOP_EVENTS.map(eventName => [eventName, stop] as const),
    ];

    for (const [eventName, handler] of subscriptions) {
      mediaElement.addEventListener(eventName, handler);
    }

    forceSyncAndSchedule();

    return () => {
      clearTimer();

      for (const [eventName, handler] of subscriptions) {
        mediaElement.removeEventListener(eventName, handler);
      }
    };
  }, [actions, chat, fallbackTimeMs, mediaElement, store]);

  return null;
}
